import 'project/polyfill'
import React from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { I18NConfig } from 'next/dist/server/config-shared'

import {
  sectionGap,
  sectionGapSmall,
  sectionPaddingLarge,
} from 'project/padding'
import { Utils } from 'common/utils'
import { getFAQList } from 'common/cms-sdk'
import { TFAQList } from 'common/cms-types'
import Button from 'components/forms/Button'
import FAQList from 'components/util/FAQList'
import { createClient } from '../../prismicio'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import HeroBanner from 'components/hero/HeroBanner'
import useWindowSize from 'components/util/useWindowSize'
import LearnMoreAu from 'components/sections/aus/LearnMoreAu'
import BuiltForDevs from 'components/cards/new/au/BuiltForDevs'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import PageContainer from 'components/util/containers/PageContainer'
import { Tenant } from 'common/tenant'
import HowYouIntegrate from 'components/cards/new/au/HowYouIntegrate'
import { auHomeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import SalesPitchCard from 'components/cards/new/common/SalesPitchCard'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import SecurityAndCompliance from 'components/cards/new/au/SecurityAndCompliance'
import EnterpriseGradeDevTool from 'components/cards/new/au/EnterpriseGradeDevTool'
import FeatureInHMMarketPlace from 'components/cards/new/au/FeatureInHMMarketPlace'
import DigitisedPostManagement from 'components/cards/new/au/DigitisedPostManagement'
import RealTimeMetricsAndReporting from 'components/cards/new/au/RealTimeMetricsAndReporting'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import { getMailCountRounded } from 'common/utils/getMailCountString'

export type PageType = {
  faqList: TFAQList
}

const Page: NextPageWithLayout<PageType> = ({ faqList }) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Enable clients to register a Sydney business address via your platform. Built for banks, accountants, and fintechs—automate mail handling and compliance.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/au/partner-integrations',
    },
    title:
      'Integrate Registered Address Solutions into Your Platform | HM Australia',
  }

  const size = useWindowSize()

  return (
    <SeoContainer
      alternate={[
        { href: '/partner-integrations', lang: 'en-gb' },
        { href: '/au/partner-integrations', lang: 'en-au' },
      ]}
      breadcrumb={auHomeBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          sectionGap,
        )}
      >
        <HeroBanner
          logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
          hideFooter
          hideDownloadButtons
          robotImage={{
            alt: 'HaiRo™ robot mascot holding phone',
            dimensions: {
              height: 963,
              width: 796,
            },
            src: '/images/new/au/robot-holding-phone.png',
          }}
          title={
            <h1>
              Helping You Offer{' '}
              <span className='color-pink2'>ASIC-Compliant</span> Registered
              Address Services at Scale
            </h1>
          }
          description={
            <div
              className='fs-fluid-18-24 lh-114 fw-normal'
              style={{
                maxWidth: 685,
              }}
            >
              We help banks, fintechs, incorporation agents, and digital service
              providers deliver compliant, Sydney-based registered business
              address services—complete with automated mail handling, branded
              portals, and a robust API.
              <br />
              <br />
              Whether you're after a no-code solution or full developer control,
              our integration tools scale with your business.
              <Link className='mx-lg-auto' href={'/au/enterprise#konnect-form'}>
                <Button className='mt-4 w-100 w-lg-auto d-block'>
                  Request a Demo
                </Button>
              </Link>
            </div>
          }
          subtitleContainerClassName='my-5'
          subtitle={
            <div className='fs-fluid-24-32 fw-semibold lh-114'>
              SaaS service built for Australian Partners: Scalable, Compliant &
              Easy to Deploy
            </div>
          }
        />

        <div
          className={classNames(
            'container d-flex flex-column',
            sectionGapSmall,
          )}
        >
          <HowYouIntegrate />
          <EnterpriseGradeDevTool />
          <RealTimeMetricsAndReporting />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div>
            <FeatureInHMMarketPlace />
            <LearnMoreAu className='mt-lg-14 mt-10' />
          </div>
          <div className='d-flex flex-column align-items-center'>
            <div
              className={classNames(
                'd-flex flex-column mb-6 mb-lg-14',
                sectionPaddingLarge,
              )}
            >
              <h2
                className={
                  'fs-fluid-36-80 lh-105 fw-semibold text-center mb-5 mb-lg-8'
                }
              >
                Trusted Across Financial and Professional Services
              </h2>
              <span className='fs-fluid-18-28 fw-semibold lh-119 lh-lg-114 color-grey5 text-center'>
                We support businesses across Australia and beyond with over{' '}
                {getMailCountRounded()} mail items securely processed.
              </span>
            </div>

            <PartnersTestimonial
              annaReview='"the integration was seamless and gave our clients peace of mind. We’ve seen strong adoption of this feature."'
              tideReview='“using The Hoxton Mix’s address service helped us offer a faster, more secure sign-up journey for businesses—boosting trust and protecting client privacy.”'
            />
            <Link className='mx-lg-auto' href={'/au/enterprise#konnect-form'}>
              <Button className='mt-15 w-100 w-lg-auto'>Book a Demo</Button>
            </Link>
          </div>
        </div>
        <div
          className={classNames(
            'container d-flex flex-column',
            sectionGapSmall,
          )}
        >
          {size.isLgOrHigher && <DigitisedPostManagement />}
          <SecurityAndCompliance />
          <BuiltForDevs />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div>
            <h1 className='fw-semibold text-center'>
              Ready to <span className='color-pink2'>Partner</span>?
            </h1>
            <SalesPitchCard
              pitchText={
                <h2 className='fs-fluid-48-40 lh-114 color-pink2'>
                  Let’s explore how we can support your platform with smart,
                  scalable address services—without operational hassle.
                </h2>
              }
              desktopImage='/images/new/au/vo-robot-pointing-right.png'
              dimensions={{
                desktop: {
                  height: 737,
                  width: 713,
                },
              }}
              cta={
                <div className='d-flex flex-column fw-semibold gap-4 mt-8'>
                  <Link href='/au/enterprise#konnect-form'>
                    <Button className='w-100 w-lg-auto'>Book a Demo</Button>
                  </Link>
                  <span className='fs-20 '>
                    or{' '}
                    <Link
                      className='text-decoration-none'
                      href='/au/contact-us'
                    >
                      Contact Us
                    </Link>
                  </span>
                </div>
              }
            />
          </div>
          <FAQList
            title={faqList.data.title}
            subtitle={faqList.data.subtitle}
            slices={faqList.data.slices}
            expandAllButton
            expandAllButtonDesktopOnly
          />
        </div>
      </main>
    </SeoContainer>
  )
}

Page.getLayout = (page: React.ReactNode) => (
  <PageContainer tenant={Tenant.AU}>{page}</PageContainer>
)

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const faqList = await getFAQList(client, lang, 'au-partner-integrations')

    return {
      props: {
        faqList,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default Page
